import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "features/auth/AuthContext";
import { getShortIdData } from "Utils/Common";

const PublicRoute = ({ children }) => {
    const { isAuthenticated, isAuthLoading, lastCompany, lastEmail } = useAuth();
    const location = useLocation();
    const params = useParams();
    const navigate = useNavigate();
    const [checking, setChecking] = useState(false);

    // Define public routes (like /login, /register, etc.)
    const publicPaths = ["/login", "/register", "/", "/signin_with_otp"];
    const isPublicPath = publicPaths.includes(location.pathname);

    useEffect(() => {
        let mounted = true;

        // Only act when auth is loaded
        if (isAuthLoading) return;

        // If user is authenticated and trying to access a public path, decide redirect
        if (isAuthenticated && isPublicPath) {
            // Special case: allow signin_with_otp / signup when short_url is present and company differs
            if ((location.pathname?.includes("/signin_with_otp") || location.pathname?.includes("/signup")) && params.short_url) {
                setChecking(true);
                getShortIdData(params.short_url)
                    .then((result) => {
                        if (!mounted) return;
                        if (result?.status && result?.data?.company_id) {
                            const newCompanyId = result.data.company_id;
                            const newEmail = result.data.email;

                            if (lastCompany !== newCompanyId && lastEmail === newEmail) {
                                // Company changed but same email -> allow login page to load so Login can call switch API
                                return;
                            }
                        }
                        // In all other cases -> redirect to /connect
                        navigate("/connect", { replace: true });
                    })
                    .catch((err) => {
                        console.error("Error in getShortIdData:", err);
                        if (mounted) navigate("/connect", { replace: true });
                    })
                    .finally(() => mounted && setChecking(false));
            } else {
                // No special case -> redirect immediately
                navigate("/connect", { replace: true });
            }
        }

        return () => {
            mounted = false;
        };
    }, [isAuthenticated, isAuthLoading, isPublicPath, location.pathname, params.short_url, lastCompany, lastEmail, navigate]);

    // While auth status or short-url check is in progress, don't render anything
    if (isAuthLoading || checking) return null;

    // If still authenticated and on a public path, redirect as a safe fallback
    if (isAuthenticated && isPublicPath) {
        return <Navigate to="/connect" replace />;
    }

    return children;
};

export default PublicRoute;
